import { apiJSON } from './http'
import type { ConnectionState, SessionResponse, AttachedPlatform } from './auth'

export interface ConnectionPayload {
  attached: AttachedPlatform[]
  connection: ConnectionState
}

export interface CredentialField {
  key: string
  label: string
  type: 'text' | 'password'
  placeholder?: string
  helpUrl?: string
}

export interface PlatformCapabilities {
  options: boolean
  crypto: boolean
  fractional: boolean
}

export interface PlatformDescriptor {
  id: string
  displayName: string
  authType: 'apiKey' | 'oauth'
  credentialFields: CredentialField[]
  capabilities: PlatformCapabilities
  // Present only for oauth platforms. The exact Callback URL the user must
  // register in their dev portal — comes from the server's SCHWAB_REDIRECT_URI.
  oauthRedirectUri?: string | null
}

export function listAvailablePlatforms(): Promise<{ platforms: PlatformDescriptor[] }> {
  return apiJSON<{ platforms: PlatformDescriptor[] }>('/api/platforms/available')
}

export function listAttachedPlatforms(): Promise<SessionResponse> {
  return apiJSON<SessionResponse>('/api/platforms/attached')
}

export function attachPlatform(
  platformId: string,
  credentials: Record<string, string>,
): Promise<SessionResponse> {
  return apiJSON<SessionResponse>('/api/platforms/attach', {
    method: 'POST',
    body: JSON.stringify({ platformId, credentials }),
  })
}

export function startPlatformOAuth(
  platformId: string,
  credentials: Record<string, string>,
): Promise<{ authorizeUrl: string }> {
  return apiJSON<{ authorizeUrl: string }>(
    `/api/platforms/${encodeURIComponent(platformId)}/oauth/start`,
    {
      method: 'POST',
      body: JSON.stringify({ credentials }),
    },
  )
}

export function reconnectPlatformOAuth(
  platformId: string,
): Promise<{ authorizeUrl: string }> {
  return apiJSON<{ authorizeUrl: string }>(
    `/api/platforms/${encodeURIComponent(platformId)}/oauth/reconnect`,
    { method: 'POST' },
  )
}

export function connectActivePlatform(): Promise<ConnectionState> {
  return apiJSON<ConnectionState>('/api/connect', { method: 'POST' })
}

export function detachPlatform(attachmentId: string): Promise<ConnectionPayload> {
  return apiJSON<ConnectionPayload>(`/api/platforms/${encodeURIComponent(attachmentId)}`, {
    method: 'DELETE',
  })
}

export function selectActiveAccount(accountId: string): Promise<ConnectionState> {
  return apiJSON<ConnectionState>('/api/accounts/active', {
    method: 'PATCH',
    body: JSON.stringify({ accountId }),
  })
}

export function updateAccountOverrides(
  accountId: string,
  overrides: { displayName?: string; displayAccountType?: string },
): Promise<ConnectionPayload> {
  return apiJSON<ConnectionPayload>(`/api/accounts/${encodeURIComponent(accountId)}/overrides`, {
    method: 'PATCH',
    body: JSON.stringify(overrides),
  })
}
